import { Period } from 'enums/shared/period';
import { OrderDetail } from 'model/modules/orders/orderDetail';
import { OtpId } from 'model/modules/otp/types';
import mapOrderDetailFromApi from 'rtkQuery/sharespro/mapper/modules/orders/mapOrderDetailFromApi';
import mapOrderListFromApi from 'rtkQuery/sharespro/mapper/modules/orders/mapOrderListFromApi';
import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import { homeApi } from './home';

export const ordersApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchOrders: builder.query<
      PaginationList<OrderListItem>,
      ApiFetchOrdersQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/common-order/index',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiOrderListItem>
        >
      ) {
        return mapOrderListFromApi(baseQueryReturnValue);
      },
    }),
    fetchOrderDetail: builder.query<OrderDetail, ID>({
      query: (id) => ({
        url: '/v3/finance/order/details',
        method: 'GET',
        sendToken: true,
        params: { id },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiOrderDetailResponse>
      ) {
        return mapOrderDetailFromApi(baseQueryReturnValue);
      },
    }),
    requestOrderConfirmation: builder.mutation<OtpId, ID>({
      query: (id) => ({
        url: '/v3/finance/order/confirm',
        method: 'POST',
        sendToken: true,
        params: { id },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiRequestOtpResponse>
      ) {
        return baseQueryReturnValue.data.otp_id;
      },
    }),
    verifyOrderConfirmation: builder.mutation<
      OrderDetail,
      ApiVerifyOrderConfirmationRequest
    >({
      query: ({ otpId, ...data }) => ({
        url: '/v3/finance/order/confirm-code',
        method: 'POST',
        sendToken: true,
        params: { id: otpId },
        body: data,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiOrderDetailResponse>
      ) {
        return mapOrderDetailFromApi(baseQueryReturnValue);
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          dispatch(
            ordersApi.util.updateQueryData(
              'fetchOrderDetail',
              data.orderId,
              () => resp.data
            )
          );
        } catch {}
      },
    }),
    requestConfirmAllOrders: builder.mutation<void, void>({
      query: () => ({
        url: '/v3/finance/order/confirm-all',
        method: 'POST',
        sendToken: true,
      }),
    }),
    verifyConfirmAllOrders: builder.mutation<void, string>({
      query: (otpCode) => ({
        url: '/v3/finance/order/confirm-all-code',
        method: 'POST',
        sendToken: true,
        body: {
          code: otpCode,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            homeApi.util.updateQueryData(
              'fetchHome',
              { period: Period.Month },
              (draft) => {
                draft.orders = [];
              }
            )
          );
        } catch {}
      },
    }),
    cancelOrder: builder.mutation<OrderDetail, ID>({
      query: (id) => ({
        url: '/v3/finance/order/cancel',
        method: 'POST',
        sendToken: true,
        params: { id },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiOrderDetailResponse>
      ) {
        return mapOrderDetailFromApi(baseQueryReturnValue);
      },
      async onQueryStarted(id, { dispatch, queryFulfilled, ...props }) {
        props.getCacheEntry();
        try {
          const resp = await queryFulfilled;
          dispatch(
            ordersApi.util.updateQueryData(
              'fetchOrderDetail',
              id,
              () => resp.data
            )
          );
        } catch {}
      },
    }),
  }),
});

export const {
  useFetchOrdersQuery,
  useFetchOrderDetailQuery,
  useCancelOrderMutation,
  useRequestOrderConfirmationMutation,
  useVerifyOrderConfirmationMutation,
  useRequestConfirmAllOrdersMutation,
  useVerifyConfirmAllOrdersMutation,
} = ordersApi;
